import { useTranslation } from 'react-i18next';
import { LegalPageShell } from '../../components/Layout/LegalPageShell';

export function PrivacyPolicy() {
  const { t } = useTranslation();
  return (
    <LegalPageShell
      title={t('legal.privacyPolicy.title')}
      lastUpdated={t('legal.privacyPolicy.lastUpdated')}
    >
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  1. Introduction
                </h2>
                <p className="text-room-text-dim mb-4">
                  Welcome to CalendIn™ ("we," "our," or "us"). We are committed to protecting your privacy and being transparent about the data we collect. This Privacy Policy explains how we collect, use, and protect your information when you use our calendar coordination service.
                </p>
                <p className="text-room-text-dim">
                  CalendIn is an instant group calendar coordination tool that helps you find overlapping availability with friends and colleagues. Our service is designed with privacy in mind—we collect minimal data and automatically delete it after 60 days.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  2. Information We Collect
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  2.1 Information You Provide
                </h3>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li><strong>Participant Names:</strong> Automatically assigned animal names (e.g., "Fox," "Bear") that you can optionally rename</li>
                  <li><strong>Availability Selections:</strong> The time slots you mark as available in calendar rooms</li>
                  <li><strong>Timezone Data:</strong> Your timezone offset to properly display availability across different timezones</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  2.2 Information Collected Automatically
                </h3>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li><strong>Browser Fingerprint:</strong> A unique identifier generated from your browser properties (user agent, language, screen size, timezone, etc.) and stored in your browser's local storage</li>
                  <li><strong>Room Metadata:</strong> Room creation time, last activity timestamp, and room IDs</li>
                  <li><strong>Timestamps:</strong> When you join rooms, mark availability, or update selections</li>
                  <li><strong>Usage Metrics:</strong> Anonymized statistics about room creation, participant counts, and feature usage for our analytics dashboard</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  2.3 Information We Do NOT Collect
                </h3>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li>Email addresses (unless you contact us directly)</li>
                  <li>Real names or personal identifiers</li>
                  <li>Payment information</li>
                  <li>IP addresses (not stored)</li>
                  <li>Precise location data</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  3. How We Use Your Information
                </h2>
                <p className="text-room-text-dim mb-4">
                  We use the collected information solely to:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li>Operate and provide the CalendIn service</li>
                  <li>Display availability overlaps across participants</li>
                  <li>Maintain room state and participant lists</li>
                  <li>Enable real-time collaboration within rooms</li>
                  <li>Prevent duplicate participant names in the same room</li>
                  <li>Generate anonymized analytics for service improvement</li>
                  <li>Calculate metrics like Monthly Active Users (MAU) and Daily Active Users (DAU)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  4. Data Storage and Security
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  4.1 Where We Store Data
                </h3>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li><strong>Server Database:</strong> Room data, participants, and availability selections are stored in our Turso (SQLite) database</li>
                  <li><strong>Local Storage:</strong> Your browser fingerprint is stored locally in your browser</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  4.2 Data Retention
                </h3>
                <p className="text-room-text-dim mb-4">
                  <strong>All room data is automatically deleted after 60 days of inactivity.</strong> This includes:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Room information</li>
                  <li>Participant names</li>
                  <li>Availability selections</li>
                  <li>All associated metadata</li>
                </ul>
                <p className="text-room-text-dim">
                  The 60-day timer extends each time there is activity in a room (someone joins or marks availability). Anonymized metrics may be retained longer for analytics purposes.
                </p>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  4.3 Security Measures
                </h3>
                <p className="text-room-text-dim mb-4">
                  We implement industry-standard security measures:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li>HTTPS encryption for all data transmission</li>
                  <li>Secure database hosting with Turso</li>
                  <li>No personal identifiable information (PII) collected</li>
                  <li>Regular security updates and monitoring</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  5. Third-Party Services and Analytics
                </h2>
                <p className="text-room-text-dim mb-4">
                  <strong>Current Status:</strong> We do not currently use third-party analytics or tracking services.
                </p>
                <p className="text-room-text-dim mb-4">
                  <strong>Future Plans:</strong> We may integrate analytics services (such as Google Analytics or Plausible) to better understand how users interact with CalendIn. If we do:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>We will update this Privacy Policy</li>
                  <li>We will only use privacy-focused analytics tools when possible</li>
                  <li>You will be notified of any changes</li>
                  <li>Refer to our Cookie Policy for information about managing analytics cookies</li>
                </ul>
                <p className="text-room-text-dim">
                  Our hosting providers (Railway for backend, Vercel for frontend, Turso for database) may collect their own analytics. Please refer to their respective privacy policies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  6. Data Sharing and Disclosure
                </h2>
                <p className="text-room-text-dim mb-4">
                  We do not sell, trade, or rent your information to third parties. We may share information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li><strong>Within Rooms:</strong> Your participant name and availability selections are visible to other participants in the same room (this is the core functionality of the service)</li>
                  <li><strong>Legal Requirements:</strong> If required by law, court order, or government request</li>
                  <li><strong>Service Providers:</strong> With hosting providers necessary to operate the service (Railway, Vercel, Turso)</li>
                  <li><strong>Business Transfer:</strong> In the event of a merger, acquisition, or sale of assets</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  7. Your Rights and Choices
                </h2>
                <p className="text-room-text-dim mb-4">
                  You have the following rights regarding your data:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li><strong>Access:</strong> You can view all your availability selections within any room you've joined</li>
                  <li><strong>Rename:</strong> You can change your participant name at any time within a room</li>
                  <li><strong>Delete:</strong> Your data is automatically deleted after 60 days, or you can clear your browser's local storage to remove your browser fingerprint</li>
                  <li><strong>Opt-Out:</strong> Simply stop using the service and your data will be deleted after 60 days of inactivity</li>
                </ul>
                <p className="text-room-text-dim">
                  For data deletion requests or questions about your data, contact us at <a href="mailto:info@calendin.app" className="text-room-accent hover:underline">info@calendin.app</a>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  8. Children's Privacy
                </h2>
                <p className="text-room-text-dim">
                  CalendIn is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us at <a href="mailto:info@calendin.app" className="text-room-accent hover:underline">info@calendin.app</a> and we will delete it promptly.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  9. International Data Transfers
                </h2>
                <p className="text-room-text-dim">
                  CalendIn is hosted on cloud infrastructure that may process and store data in various locations worldwide. By using our service, you consent to the transfer of your information to countries that may have different data protection laws than your country of residence.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  10. Changes to This Privacy Policy
                </h2>
                <p className="text-room-text-dim mb-4">
                  We may update this Privacy Policy from time to time. When we do:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>We will update the "Last updated" date at the top of this policy</li>
                  <li>Significant changes will be communicated through the service</li>
                  <li>Your continued use of CalendIn after changes constitutes acceptance of the updated policy</li>
                </ul>
                <p className="text-room-text-dim">
                  We encourage you to review this Privacy Policy periodically.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  11. Contact Us
                </h2>
                <p className="text-room-text-dim mb-4">
                  If you have questions, concerns, or requests regarding this Privacy Policy or your data, please contact us:
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-room-text-dim">
                    <strong>Email:</strong> <a href="mailto:info@calendin.app" className="text-room-accent hover:underline">info@calendin.app</a>
                  </p>
                  <p className="text-room-text-dim mt-2">
                    <strong>Service:</strong> CalendIn
                  </p>
                </div>
              </section>
    </LegalPageShell>
  );
}

